import { SelectChangeEvent } from '@mui/material';
import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { type DropdownListItem } from '@verantos-dev/shared-components';

import { useAppDispatch, useAppSelector } from '../../../lib/store';
import { useGetSupervisorProjectsQuery } from '../../supervisor-queue/api/supervisorQueueApiSlice';
import { resetAllStates, setSelectedProjectId } from '../state/metricsSlice';
import { selectSelectedProjectId } from '../state/selector';

type DropdownChangeHandler = (
  event: SelectChangeEvent<unknown> | (Event & { target: { value: unknown; name: string } }),
  child: ReactNode
) => void;

export interface UseSupervisorQueueContentResult {
  projectOptions: DropdownListItem[];
  isProjectsLoading: boolean;
  handleProjectChange: DropdownChangeHandler;
  selectedProjectId: string | null;
}

export const useMetricProjectsDropdown = (): UseSupervisorQueueContentResult => {
  const dispatch = useAppDispatch();

  const selectedProjectId = useAppSelector(selectSelectedProjectId);
  const {
    data: projectsData,
    isLoading: isProjectsLoading,
    isFetching: isProjectsFetching,
  } = useGetSupervisorProjectsQuery();

  // Convert projects data to dropdown options format
  const projectOptions: DropdownListItem[] = useMemo(() => {
    if (!projectsData?.data || projectsData.data.length === 0) {
      return [];
    }
    return projectsData.data.map((project) => ({
      value: String(project.id),
      displayValue: project.label,
    }));
  }, [projectsData]);

  const handleProjectChange: DropdownChangeHandler = (event) => {
    event.preventDefault();
    const value = event.target?.value;
    if (typeof value === 'string') {
      dispatch(setSelectedProjectId(value));
      dispatch(resetAllStates());
    }
  };

  return {
    projectOptions,
    isProjectsLoading: isProjectsLoading || isProjectsFetching,
    handleProjectChange,
    selectedProjectId: selectedProjectId ?? null,
  };
};
